import { Button } from '@repo/studio-design-system-ui-button'
import { useIsCompactViewport, useIsSmallViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import { useChangeLanguageMutation } from '@repo/studio-i18n-hooks/use-change-language'
import { useLanguagePrefetch } from '../../hooks/use-language-prefetch'
import { useUserQuery } from '@repo/cf-user/hooks/auth'
import { defaultLanguage, studioSelectableLanguageMap } from 'cf-i18n/constants'
import { useStudioLangCookie } from 'cf-i18n/cookies'
import { toLanguageBadgeCode } from 'cf-i18n/helpers'
import { AnimatePresence, m, useReducedMotion } from 'framer-motion'
import { Search } from 'primitives/form-controls/search'
// oxlint-disable-next-line cf-web/banned-imports
import { CheckIcon, CrossIcon } from 'primitives/icons'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { cx } from 'utils/classnames'
import { MenuRow } from '@repo/studio-components-menu-row'
import { IconHolder } from '@repo/studio-components-icon-holder'

const EASE_CUSTOM = [0.32, 0.72, 0, 1] as const

const enterTransition = { duration: 0.2, ease: EASE_CUSTOM }
const exitTransition = { duration: 0.15, ease: EASE_CUSTOM }

function getPortalTarget(isVertical: boolean, navSheetEl: HTMLElement | null) {
  if (typeof document === 'undefined') {
    return null
  }
  return isVertical ? navSheetEl : document.body
}

function getPanelPositionClass(isVertical: boolean, isSmallViewport: boolean) {
  if (!isVertical) {
    return 'fixed bottom-2 h-[480px] w-[755px] max-h-[480px] max-w-[755px] rounded-4 origin-bottom-left'
  }
  if (isSmallViewport) {
    return 'absolute top-0 bottom-0 left-0 w-[245px]'
  }
  return 'absolute top-0 bottom-0 left-full w-[245px]'
}

export function LanguageSwitcher({
  className,
  layout,
}: {
  className?: string
  layout: 'horizontal' | 'vertical'
}) {
  const isCompactViewport = useIsCompactViewport()
  const isSmallViewport = useIsSmallViewport()
  const [currLangCookie] = useStudioLangCookie()
  const userLanguageQuery = useUserQuery(undefined, { select: data => data?.me?.user?.language })
  const changeLanguageMutation = useChangeLanguageMutation()
  const [languageFilter, setLanguageFilter] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [navSheetEl, setNavSheetEl] = React.useState<HTMLElement | null>(null)

  const isVertical = layout === 'vertical'
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const selectedItemRef = React.useRef<HTMLButtonElement>(null)
  const compactViewportRef = React.useRef(isCompactViewport)

  const currLang = userLanguageQuery.data || currLangCookie || defaultLanguage

  const { prefetchOnHoverIntent, cancelHoverPrefetch } = useLanguagePrefetch({
    open,
    currentLanguage: currLang,
  })

  const filteredLanguages = [...studioSelectableLanguageMap]
    .filter(([, label]) => label.toLowerCase().includes(languageFilter.toLowerCase()))
    .toSorted(([idA, a], [idB, b]) => {
      if (idA === defaultLanguage) {
        return -1
      }
      if (idB === defaultLanguage) {
        return 1
      }
      return a.localeCompare(b, 'en', { sensitivity: 'base' })
    })

  // Vertical chrome is a prop; this only finds the sheet dialog so the panel
  // can portal out of `overflow-auto` and still position with `left-full`.
  React.useLayoutEffect(() => {
    if (!isVertical) {
      setNavSheetEl(null)
      return
    }
    setNavSheetEl(triggerRef.current?.closest('[role="dialog"]') ?? null)
  }, [isVertical, open])

  React.useEffect(() => {
    if (compactViewportRef.current === isCompactViewport) {
      return
    }
    compactViewportRef.current = isCompactViewport
    setOpen(false)
  }, [isCompactViewport])

  React.useEffect(() => {
    if (!open) {
      return
    }

    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  React.useEffect(() => {
    if (!open) {
      setLanguageFilter('')
    }
  }, [open])

  React.useEffect(() => {
    if (!open) {
      return
    }

    const list = listRef.current
    const selectedItem = selectedItemRef.current
    if (!list || !selectedItem) {
      return
    }

    // Jump straight to the current language instead of making users scroll
    // through the full list to find it every time the panel opens. Adjusting
    // the list's own scrollTop rather than calling `scrollIntoView` keeps every
    // ancestor scroller untouched — `scrollIntoView` walks the whole chain up to
    // the document, which shifts the page behind the mobile navigation sheet.
    const listRect = list.getBoundingClientRect()
    const itemRect = selectedItem.getBoundingClientRect()
    list.scrollTop += itemRect.top - listRect.top - (listRect.height - itemRect.height) / 2
  }, [open])

  function handleSelectLanguage(id: typeof currLang) {
    setOpen(false)
    changeLanguageMutation.mutate({ language: id })
  }

  const prefersReducedMotion = useReducedMotion()
  const portalTarget = getPortalTarget(isVertical, navSheetEl)

  const panelVariants = prefersReducedMotion
    ? {
        hidden: { opacity: 0, transition: exitTransition },
        visible: { opacity: 1, transition: enterTransition },
      }
    : isVertical
      ? {
          hidden: { x: '-100%', opacity: 0, transition: exitTransition },
          visible: { x: 0, opacity: 1, transition: enterTransition },
        }
      : {
          hidden: { opacity: 0, scale: 0.96, y: -8, transition: exitTransition },
          visible: { opacity: 1, scale: 1, y: 0, transition: enterTransition },
        }

  const panel = (
    <AnimatePresence>
      {open ? (
        <m.div
          ref={panelRef}
          variants={panelVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className={cx(
            'z-10 flex min-h-0 flex-col overflow-hidden bg-gray-1000 shadow-3-smooth',
            getPanelPositionClass(isVertical, isSmallViewport),
            !isVertical && className,
          )}
        >
          {isVertical ? (
            <div className="flex items-center justify-between p-3 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <span className="typography-16 font-medium text-gray-50">Language</span>
              </div>
              <Button
                iconOnly
                size="xs"
                variant="secondary"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <CrossIcon />
              </Button>
            </div>
          ) : null}

          {/* Search */}
          <div className="p-3 border-b border-gray-800">
            <Search
              placeholder="Search language"
              onChange={e => setLanguageFilter(e.currentTarget.value)}
              value={languageFilter}
            />
          </div>

          {/* Language list */}
          <div
            ref={listRef}
            className={cx(
              'min-h-0 flex-1 gap-1 overflow-y-auto p-3 scroller-xs scroller-persist',
              isVertical ? 'flex flex-col' : 'grid grid-cols-3 content-start',
            )}
          >
            {filteredLanguages.map(([id, label]) => {
              const selected = id === currLang

              return (
                <MenuRow
                  key={id}
                  ref={selected ? selectedItemRef : undefined}
                  variant="text-only"
                  selected={selected}
                  className={selected ? 'bg-studio-gray-800 hover:bg-studio-gray-800' : undefined}
                  onClick={() => handleSelectLanguage(id)}
                  onFocus={() => prefetchOnHoverIntent(id)}
                  onPointerEnter={() => prefetchOnHoverIntent(id)}
                  onPointerLeave={cancelHoverPrefetch}
                >
                  <span className="typography-14 font-medium">{label}</span>
                  {selected ? (
                    <IconHolder size="medium" className="ml-auto">
                      <CheckIcon className="text-gray-400" />
                    </IconHolder>
                  ) : (
                    <span className="ml-auto bg-gray-900 rounded-full px-2 py-[5px] typography-10 text-gray-400 uppercase font-mono">
                      {toLanguageBadgeCode(id)}
                    </span>
                  )}
                </MenuRow>
              )
            })}
          </div>
        </m.div>
      ) : null}
    </AnimatePresence>
  )

  return (
    <>
      <Button
        iconOnly
        ref={triggerRef}
        aria-label="Language"
        aria-expanded={open}
        aria-haspopup="true"
        size="sm"
        variant="secondary"
        className="typography-11"
        onClick={() => setOpen(prev => !prev)}
      >
        {toLanguageBadgeCode(currLang)}
      </Button>
      {portalTarget ? createPortal(panel, portalTarget) : panel}
    </>
  )
}
